import { useEffect, useRef, useState } from "react";
import { Upload, Sparkles, ScanFace } from "lucide-react";
import * as vault from "../lib/vault";
import { readJsonFile } from "../lib/backupFile";
import PinPad from "../components/PinPad";
import LockShell, { faceIdErrorMessage } from "../components/LockShell";

// First run: restore a backup (or start empty), choose a PIN, offer Face ID.
export default function Setup({ onStart, onDone }) {
  const [step, setStep] = useState("welcome");
  const [initialData, setInitialData] = useState(null);
  const [firstPin, setFirstPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [faceIdOk, setFaceIdOk] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    vault.faceIdAvailable().then(setFaceIdOk);
  }, []);

  const pickBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setInitialData(vault.parseBackup(await readJsonFile(file)));
      setError("");
      setStep("pin");
    } catch {
      setError("That file isn't a Nest backup.");
    }
  };

  const onFirstPin = (pin) => {
    setFirstPin(pin);
    setError("");
    setStep("confirm");
  };

  const onConfirmPin = async (pin) => {
    if (pin !== firstPin) {
      setError("PINs didn't match — try again");
      setResetKey((k) => k + 1);
      setStep("pin");
      return;
    }
    setBusy(true);
    // vault.setup unlocks the app; onStart keeps this screen up so Face ID can be offered.
    onStart();
    await vault.setup(pin, initialData);
    setBusy(false);
    if (faceIdOk) setStep("faceid");
    else finish();
  };

  const finish = () => onDone();

  const turnOnFaceId = async () => {
    setBusy(true);
    setError("");
    try {
      await vault.enableFaceId();
      finish();
    } catch (err) {
      setError(faceIdErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (step === "welcome") {
    return (
      <LockShell
        title="Welcome to Nest"
        subtitle="Your finances live only on this phone, encrypted and locked with your PIN or Face ID."
      >
        <div className="w-full max-w-xs space-y-3">
          <button
            onClick={() => fileRef.current?.click()}
            className="nest-btn-primary w-full flex items-center justify-center gap-2 py-3"
            data-testid="restore-backup-btn"
          >
            <Upload size={16} /> Restore from backup
          </button>
          <button
            onClick={() => {
              setInitialData(null);
              setStep("pin");
            }}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-full border border-nest-line nest-glass text-sm"
            data-testid="start-fresh-btn"
          >
            <Sparkles size={16} strokeWidth={1.5} /> Start fresh
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={pickBackup}
            data-testid="restore-file-input"
          />
          {error && <p className="text-sm text-nest-warn text-center">{error}</p>}
        </div>
      </LockShell>
    );
  }

  if (step === "pin" || step === "confirm") {
    const confirming = step === "confirm";
    return (
      <LockShell
        title={confirming ? "Confirm your PIN" : "Choose a 6-digit PIN"}
        subtitle={
          confirming
            ? "Enter it once more."
            : initialData
              ? `Backup loaded: ${initialData.expenses.length} expenses, ${initialData.wealth.length} wealth snapshots. Now pick a PIN.`
              : "You'll use this when Face ID isn't available. It can't be recovered, so keep backups."
        }
      >
        <PinPad
          key={step}
          onComplete={confirming ? onConfirmPin : onFirstPin}
          busy={busy}
          error={error}
          resetKey={resetKey}
        />
      </LockShell>
    );
  }

  if (step === "faceid") {
    return (
      <LockShell title="Unlock with Face ID?" subtitle="Faster than typing your PIN. You can change this later in Settings.">
        <div className="w-full max-w-xs space-y-3">
          <button
            onClick={turnOnFaceId}
            disabled={busy}
            className="nest-btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-60"
            data-testid="enable-faceid-btn"
          >
            <ScanFace size={18} strokeWidth={1.5} /> Use Face ID
          </button>
          <button
            onClick={finish}
            className="w-full py-3 text-sm text-nest-muted"
            data-testid="skip-faceid-btn"
          >
            Not now
          </button>
          {error && <p className="text-sm text-nest-warn text-center">{error}</p>}
        </div>
      </LockShell>
    );
  }
}
